import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  BookOpen, 
  Settings, 
  Plus, 
  Calendar, 
  TrendingUp, 
  Users,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useClasses } from '../hooks/useClasses';
import { useEnrollments } from '../hooks/useEnrollments';
import { 
  useGradingSettings, 
  useClassGrades,
  useCreateGrade,
  useReportingPeriods
} from '../hooks/useGrading';
import GradebookTable from '../components/grading/GradebookTable';
import AddGradeModal from '../components/grading/AddGradeModal';
import GradingSettingsModal from '../components/grading/GradingSettingsModal';
import ReportingPeriodsModal from '../components/grading/ReportingPeriodsModal';

const Grading = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedClassId, setSelectedClassId] = useState(searchParams.get('class') || '');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPeriodsModal, setShowPeriodsModal] = useState(false);

  // Fetch data
  const { data: classesData } = useClasses();
  const { data: settings } = useGradingSettings(selectedClassId);
  const { data: gradesData, isLoading: gradesLoading } = useClassGrades(selectedClassId);
  const { data: periodsData } = useReportingPeriods(selectedClassId);
  // const { data: summariesData } = useGradeSummaries(selectedClassId);
  const { data: enrollmentsData } = useEnrollments({ classId: selectedClassId });

  const createGrade = useCreateGrade();

  const classes = classesData?.classes || [];
  const grades = React.useMemo(() => gradesData?.grades || [], [gradesData]);
  const periods = periodsData?.periods || [];
  // const summaries = summariesData?.summaries || [];
  const enrollments = enrollmentsData?.enrollments || [];

  const selectedClass = classes.find(c => c.id === selectedClassId);
  const isGradingEnabled = settings?.gradingEnabled;

  const handleClassChange = (classId) => {
    setSelectedClassId(classId);
    setSearchParams(classId ? { class: classId } : {});
  };

  const handleAddGrade = async (gradeData) => {
    try {
      await createGrade.mutateAsync({
        classId: selectedClassId,
        data: gradeData
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Failed to add grade:', err);
    }
  };

  // Calculate statistics
  const gradeStats = React.useMemo(() => {
    if (!grades.length) return null;
    
    const percentages = grades.map(g => g.percentage).filter(Boolean);
    const average = percentages.length 
      ? (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(1)
      : 0;
    
    const passingCount = grades.filter(g => g.isPassing).length;
    
    return {
      average,
      totalGrades: grades.length,
      passingCount,
      failingCount: grades.length - passingCount
    };
  }, [grades]);

  if (!selectedClassId) {
    return (
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Grading</h1>
          <p className="text-gray-500 mt-1">Manage grades and assessments for your classes.</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-12 text-center">
          <BookOpen className="mx-auto h-16 w-16 text-purple-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Select a Class</h2>
          <p className="text-gray-500 mb-6">Choose a class to view and manage grades.</p>
          
          <div className="max-w-md mx-auto">
            <select
              value={selectedClassId}
              onChange={(e) => handleClassChange(e.target.value)}
              className="form-input"
            >
              <option value="">Select a class...</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {selectedClass?.name || 'Grading'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isGradingEnabled 
              ? 'Grading is enabled for this class' 
              : 'Grading is not yet configured for this class'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Class Selector */}
          <select
            value={selectedClassId}
            onChange={(e) => handleClassChange(e.target.value)}
            className="form-input w-full sm:min-w-[200px]"
          >
            <option value="">Select class...</option>
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>{cls.name}</option>
            ))}
          </select>

          {/* Period Selector */}
          {periods.length > 0 && (
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="form-input"
            >
              <option value="all">All Periods</option>
              {periods.map(period => (
                <option key={period.id} value={period.id}>{period.periodName}</option>
              ))}
            </select>
          )}

          {/* Action Buttons */}
          <button
            onClick={() => setShowPeriodsModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Calendar size={18} />
            <span>Periods</span>
          </button>

          <button
            onClick={() => setShowSettingsModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Settings size={18} />
            <span>Settings</span>
          </button>

          {isGradingEnabled && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Grade</span>
            </button>
          )}
        </div>
      </div>

      {/* Not Enabled Warning */}
      {!isGradingEnabled && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-medium text-amber-900">Grading Not Enabled</h3>
            <p className="text-amber-700 text-sm mt-1">
              Grading is not yet configured for this class. Click "Settings" to enable grading and configure your grading scale.
            </p>
          </div>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="btn-primary text-sm"
          >
            Enable Grading
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {gradeStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Class Average</p>
                <p className="text-2xl font-bold text-purple-600">{gradeStats.average}%</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Grades</p>
                <p className="text-2xl font-bold text-gray-900">{gradeStats.totalGrades}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <BookOpen className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Passing</p>
                <p className="text-2xl font-bold text-green-600">{gradeStats.passingCount}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Users className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Needs Attention</p>
                <p className="text-2xl font-bold text-red-600">{gradeStats.failingCount}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <AlertCircle className="text-red-600" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gradebook */}
      {isGradingEnabled && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          {gradesLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-purple-600" size={32} />
            </div>
          ) : (
            <GradebookTable 
              grades={grades}
              enrollments={enrollments}
              classId={selectedClassId}
              settings={settings}
            />
          )}
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddGradeModal
          classId={selectedClassId}
          enrollments={enrollments}
          settings={settings}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddGrade}
        />
      )}

      {showSettingsModal && (
        <GradingSettingsModal
          classId={selectedClassId}
          settings={settings}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      {showPeriodsModal && (
        <ReportingPeriodsModal
          classId={selectedClassId}
          periods={periods}
          onClose={() => setShowPeriodsModal(false)}
        />
      )}
    </div>
  );
};

export default Grading;
